import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";

/**
 * Capture / preview viewport sizes for App Store & Play Store screenshot galleries.
 * Google Play sizes match Play Console phone / 7" / 10" tablet upload dimensions.
 * Jigsaw, Sliding, and Word Wheel also have a PC chip at 1920×1080 (16:9) for Google Play on PC.
 * Apple sizes match App Store Connect (iPhone 6.7" / iPad Pro 13").
 */
export const SCREENSHOT_DEVICES = {
    phone: {
        label: "Phone",
        width: 1080,
        height: 1920,
        group: "google",
        aspectHint: "9:16",
    },
    tablet7: {
        label: '7" tablet',
        width: 1080,
        height: 1728,
        group: "google",
        aspectHint: "5:8",
    },
    tablet10: {
        label: '10" tablet',
        width: 1080,
        height: 1728,
        group: "google",
        aspectHint: "5:8",
    },
    pc: {
        label: "PC",
        width: 1920,
        height: 1080,
        group: "google",
        aspectHint: "16:9",
        apps: ["jigsaw", "sliding", "word-wheel"],
    },
    iphone67: {
        label: 'iPhone 6.7"',
        width: 1290,
        height: 2796,
        group: "apple",
        fileHint: 'iPhone 6.7"-*.png',
    },
    ipadPro13: {
        label: 'iPad Pro 13"',
        width: 2064,
        height: 2752,
        group: "apple",
        fileHint: 'iPad Pro 13"-*.png',
    },
};

export const DEFAULT_SCREENSHOT_DEVICE = "phone";

const DEVICE_GROUPS = [
    { id: "google", title: "Google Play" },
    { id: "apple", title: "Apple" },
];

export function resolveScreenshotDevice(value) {
    return SCREENSHOT_DEVICES[value] ? value : DEFAULT_SCREENSHOT_DEVICE;
}

function deviceHref(pathname, search, device) {
    const params = new URLSearchParams(search);
    params.set("device", device);
    return `${pathname}?${params.toString()}`;
}

function DeviceChipRow({ entries, selectedDevice, pathname, search }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {entries.map(([key, config]) => {
                const selected = key === selectedDevice;
                return (
                    <Chip
                        key={key}
                        component={RouterLink}
                        to={deviceHref(pathname, search, key)}
                        clickable
                        size="small"
                        label={`${config.label} · ${config.width}×${config.height}${
                            config.aspectHint ? ` · ${config.aspectHint}` : ""
                        }`}
                        color={selected ? "primary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        title={
                            [config.aspectHint, config.fileHint].filter(Boolean).join(" · ") ||
                            undefined
                        }
                        sx={{
                            fontWeight: selected ? 700 : 500,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "none" },
                        }}
                    />
                );
            })}
        </Box>
    );
}

/** Device-size switcher for screenshot gallery previews and capture windows. */
export default function ScreenshotDevicePicker({ device, sx } = {}) {
    const { pathname, search } = useLocation();
    const { app } = useParams();
    const selectedDevice = resolveScreenshotDevice(device);

    return (
        <Box sx={{ mb: 2, ...sx }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Device
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {DEVICE_GROUPS.map((group) => {
                    const entries = Object.entries(SCREENSHOT_DEVICES).filter(([, config]) => {
                        if (config.group !== group.id) return false;
                        if (config.apps && !config.apps.includes(app)) return false;
                        return true;
                    });
                    if (!entries.length) return null;
                    return (
                        <Box key={group.id}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
                            >
                                {group.title}
                            </Typography>
                            <DeviceChipRow
                                entries={entries}
                                selectedDevice={selectedDevice}
                                pathname={pathname}
                                search={search}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
